import React, { useMemo } from 'react';
import WrapperDataGrid from '@modules/dataGrid/WrapperDataGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import {
  TInheritedProps,
  generateGlobalColumns,
  generateBasicColumn,
  generateDateColumn,
  generateEntityColumn,
  IDataGridConfig,
  IAction,
} from '@ts/storybook';
import actStore from '@flux/actions/actStore';
import { Box, Stack } from '@mui/material';
import Dot from '@components/Utils/Dot';
import { IConfigComponent, CEntity, CLocation, AllocationCheckResultValueType, IAllocationCheck } from '@ts/ui-models';

interface IAllocationsCheckProps {
  itemId?: string;
  configComponent?: IConfigComponent;
  inheritedProps?: TInheritedProps<false>;
}

export const defaultConfigComponent: IConfigComponent = {
  componentId: 'allocationChecks',
  aclId: 'AllocationCheck',
  //entity: 'allocationCheck',
  fetchURL: '/api/v2/allocationChecks',
  crudURL: '/api/v2/allocationCheck', //locationRule
};

const AllocationsCheck = (props: IAllocationsCheckProps) => {
  const { configComponent = defaultConfigComponent, inheritedProps } = props;
  const config: IDataGridConfig = {
    columnVisibilityModel: { id: false, event: false },
  };

  const extraAction = (): IAction => {
    return {
      id: 'locationRules',
      label: 'Location Rules',
      icon: 'manage_search',
      onClick: handleClick => {
        const entity = new CEntity(handleClick());
        actStore.setActionTableV3({ action: 'openModalLocationRules', row: entity });
      },
    };
  };

  const getColumns = useMemo((): GridColDef<any>[] => {
    const mixProps = { ...props, configComponent };
    const globalCells = generateGlobalColumns(mixProps);
    return [
      globalCells.id(),
      globalCells.name(),
      {
        headerName: 'Status',
        field: 'status',
        valueGetter: (value, row, column) => {
          return row.result;
        },

        renderCell: (params: GridRenderCellParams) => {
          const alloc: IAllocationCheck = params.row;
          let color: string;
          switch (alloc.result) {
            case AllocationCheckResultValueType.Ok:
              color = 'success';
              break;
            case AllocationCheckResultValueType.InVariance:
              color = 'warning';
              break;
            case AllocationCheckResultValueType.Fail:
              color = 'error';
              break;
            default:
              color = 'primary';
          }

          return (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                pl: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Dot size={16} color={color} />
              </Stack>
            </Box>
          );
        },
      },
      generateEntityColumn({ field: 'location' }, { field: 'location', mainClass: CLocation }, { inheritedProps }),
      generateBasicColumn({ field: 'updaterId', headerName: 'User' }, { field: 'updaterId' }),
      generateDateColumn({ field: 'timestamp', headerName: 'Date' }, { field: 'timestamp' }),
    ];
  }, [props, configComponent]);

  return (
    <WrapperDataGrid
      configComponent={configComponent}
      dataGridConfig={config}
      columns={getColumns}
      //Extra data
      withoutDefaultActions
      extraActions={[extraAction()]}
      inheritedProps={props.inheritedProps}
    />
  );
};

export default AllocationsCheck;
